import { TicketInboxFilter } from "@jugl-web/rest-api/tickets/models/common-types/TicketInboxFilter";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { FC, ReactNode, SVGProps } from "react";
import { cx, useAppVariant } from "@jugl-web/utils";
import { ReactComponent as UnassignedIcon } from "./assets/unassigned.svg";
import { ReactComponent as AssignedToMeIcon } from "./assets/assigned-to-me.svg";
import emptyStateAnimation from "./assets/empty-state-animation.json";
import { ReactComponent as InProgressIcon } from "./assets/in-progress.svg";
import { ReactComponent as IssuedIcon } from "./assets/issued.svg";
import { ReactComponent as OnCustomerIcon } from "./assets/on-customer.svg";
import { ReactComponent as OnHoldIcon } from "./assets/on-hold.svg";
import { ReactComponent as ResolvedIcon } from "./assets/resolved.svg";

interface TicketsEmptyStateProps {
  variant: TicketInboxFilter;
  createAction?: ReactNode;
}

export const TicketsEmptyState: FC<TicketsEmptyStateProps> = ({
  variant,
  createAction,
}) => {
  const { isMobile } = useAppVariant();
  const { title, subtitle } = variantToCopy[variant] ?? FALLBACK_COPY;

  const IconComponent = variantToIcon[variant];

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div
        className={cx(
          "flex flex-col items-center text-center",
          isMobile ? "max-w-[250px] gap-6" : "max-w-[500px] gap-8"
        )}
      >
        {IconComponent ? (
          <div
            className={cx(
              "relative flex items-center justify-center",
              isMobile
                ? "size-[120px] drop-shadow-[-14.175px_15.75px_18.585px_rgba(87,68,111,0.16)]"
                : "size-[150px] drop-shadow-[-17.719px_19.687px_11.616px_rgba(87,68,111,0.16)]"
            )}
          >
            <div
              className={cx(
                "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white",
                isMobile ? "size-[100px]" : "size-[126px]"
              )}
            />
            <IconComponent
              aria-hidden
              className={cx(
                "relative shrink-0",
                isMobile ? "size-[88px]" : "size-[110px]"
              )}
            />
          </div>
        ) : (
          <Lottie
            options={{ animationData: emptyStateAnimation }}
            width={150}
            height={150}
          />
        )}

        <div className="flex flex-col items-center gap-2">
          <h2
            className={cx(
              "m-0 font-secondary leading-normal font-semibold text-dark",
              isMobile ? "text-xl" : "text-2xl"
            )}
          >
            {title}
          </h2>
          <p
            className={cx(
              "m-0 font-secondary leading-normal text-dark-700",
              isMobile ? "text-sm" : "text-base"
            )}
          >
            {subtitle}
          </p>
        </div>

        {createAction}
      </div>
    </div>
  );
};

const variantToIcon: Record<
  TicketInboxFilter,
  FC<SVGProps<SVGSVGElement>> | null
> = {
  all: null,
  assigned_to_user: AssignedToMeIcon,
  unassigned: UnassignedIcon,
  "todo-today": null,
  recents: null,
  in_progress: InProgressIcon,
  on_customer: OnCustomerIcon,
  on_hold: OnHoldIcon,
  issued: IssuedIcon,
  resolved: ResolvedIcon,
};

const variantToCopy: Record<
  TicketInboxFilter,
  { title: string; subtitle: string }
> = {
  all: {
    title: "No open tickets",
    subtitle: "Your team have no active tickets at the moment",
  },
  unassigned: {
    title: "No open tickets",
    subtitle: "Your team have no unassigned tickets at the moment",
  },
  assigned_to_user: {
    title: "No open tickets",
    subtitle: "There are no tickets assigned to you at the moment",
  },
  "todo-today": {
    title: "No open tickets",
    subtitle: `There are no "To-Do Today" tickets at the moment`,
  },
  recents: {
    title: "No open tickets",
    subtitle: `There are no "Recents" tickets at the moment`,
  },
  in_progress: {
    title: "No open tickets",
    subtitle: `There are no "In Progress" tickets at the moment`,
  },
  on_customer: {
    title: "No open tickets",
    subtitle: `There are no "On Customer" tickets at the moment`,
  },
  on_hold: {
    title: "No open tickets",
    subtitle: `There are no "On Hold" tickets at the moment`,
  },
  issued: {
    title: "No open tickets",
    subtitle: `There are no "Issued" tickets at the moment`,
  },
  resolved: {
    title: "No resolved tickets",
    subtitle: `There are no "Resolved" tickets at the moment`,
  },
};

const FALLBACK_COPY = {
  title: "No tickets",
  subtitle: "There are no tickets at the moment",
};
